/**
 * A person's own column layout for a table: which columns show, and in
 * what order.
 *
 * PURE, and separate from both the table and the preference store, so
 * the rules below can be tested without rendering anything -- they are
 * the part that has to survive a release adding or removing a column
 * while somebody's saved layout sits in their browser.
 *
 * WHAT IS STORED is the whole order as the person last left it plus the
 * ids they hid, never a diff against the default. That is what lets the
 * merge tell a column the person has SEEN and placed apart from one a
 * later release added: an id the saved layout does not mention is new to
 * them, and takes its default place and visibility rather than being
 * dumped at the end or hidden because it was not in a list.
 */

/** What is persisted per table (see `UserPreferences.tableLayouts`). */
export interface TableLayout {
  order: string[];
  hidden: string[];
}

/** What a table declares about each of its columns, in DEFAULT order. */
export interface LayoutColumnSpec {
  id: string;
  defaultVisible: boolean;
  /** False for a column the table cannot be read without (the Tasks
   *  table's Status): it shows whatever a saved layout says. */
  hideable: boolean;
}

export interface ResolvedColumn {
  id: string;
  visible: boolean;
}

function defaultVisibility(spec: LayoutColumnSpec): boolean {
  return spec.defaultVisible || !spec.hideable;
}

/**
 * The layout to draw: `saved` laid over the table's defaults.
 *
 *  - an id `saved` names that the table no longer has is dropped;
 *  - a column `saved` does not mention (added since it was written) goes
 *    in right after its nearest default-order predecessor that is
 *    present, or first if it has none, with its default visibility;
 *  - an unhideable column is visible whatever `saved` says.
 */
export function resolveTableLayout(
  specs: readonly LayoutColumnSpec[],
  saved: TableLayout | null | undefined,
): ResolvedColumn[] {
  if (!saved) return specs.map((s) => ({ id: s.id, visible: defaultVisibility(s) }));

  const byId = new Map(specs.map((s) => [s.id, s]));
  const order: string[] = [];
  for (const id of saved.order) {
    if (byId.has(id) && !order.includes(id)) order.push(id);
  }
  const hidden = new Set(saved.hidden.filter((id) => byId.has(id)));
  // A column counts as KNOWN to the saved layout if it was placed or
  // hidden in it. Only an unknown one gets default treatment.
  const known = new Set([...order, ...hidden]);

  specs.forEach((spec, i) => {
    if (order.includes(spec.id)) return;
    let at = 0;
    for (let j = i - 1; j >= 0; j--) {
      const prev = order.indexOf(specs[j]!.id);
      if (prev !== -1) {
        at = prev + 1;
        break;
      }
    }
    order.splice(at, 0, spec.id);
  });

  return order.map((id) => {
    const spec = byId.get(id)!;
    if (!spec.hideable) return { id, visible: true };
    return { id, visible: known.has(id) ? !hidden.has(id) : spec.defaultVisible };
  });
}

/** What to persist for a resolved layout. */
export function layoutToSave(resolved: readonly ResolvedColumn[]): TableLayout {
  return {
    order: resolved.map((c) => c.id),
    hidden: resolved.filter((c) => !c.visible).map((c) => c.id),
  };
}

/**
 * True when `resolved` is exactly what the table shows with nothing
 * saved. The caller stores NOTHING in that case rather than a copy of
 * today's default, so a later release that changes the default reaches
 * this person too instead of being pinned by a layout they never chose.
 */
export function isDefaultLayout(
  specs: readonly LayoutColumnSpec[],
  resolved: readonly ResolvedColumn[],
): boolean {
  const def = resolveTableLayout(specs, null);
  return (
    def.length === resolved.length &&
    def.every((c, i) => c.id === resolved[i]!.id && c.visible === resolved[i]!.visible)
  );
}

/** Swap `id` with its neighbour one place earlier (-1) or later (+1).
 *  A move off either end returns the layout unchanged. */
export function moveColumn(
  resolved: readonly ResolvedColumn[],
  id: string,
  delta: -1 | 1,
): ResolvedColumn[] {
  const from = resolved.findIndex((c) => c.id === id);
  const to = from + delta;
  if (from === -1 || to < 0 || to >= resolved.length) return [...resolved];
  const next = [...resolved];
  [next[from], next[to]] = [next[to]!, next[from]!];
  return next;
}

/** Show or hide `id`. Refused (layout unchanged) for an unhideable column. */
export function setColumnVisible(
  specs: readonly LayoutColumnSpec[],
  resolved: readonly ResolvedColumn[],
  id: string,
  visible: boolean,
): ResolvedColumn[] {
  const spec = specs.find((s) => s.id === id);
  if (!spec || (!spec.hideable && !visible)) return [...resolved];
  return resolved.map((c) => (c.id === id ? { ...c, visible } : c));
}
